import { ReactNode } from 'react';
import { Chip } from '../primitives';
import {
    FREQUENCY_OPTIONS, Idea, TYPE_LABEL, formatHours, hoursPerYear,
} from '../data/improvementBoard';

const Answer = ({ question, children }: { question: string, children: ReactNode }) => (
    <div className="flex flex-col gap-1">
        <span className="ds-eyebrow">{question}</span>
        <p className="m-0" style={{ whiteSpace: 'pre-wrap' }}>{children}</p>
    </div>
);

/** Body of the detail dialog - every answer as it was asked, plus the arithmetic. */
export const ImprovementDetail = ({ idea }: { idea: Idea }) => {
    const freq = FREQUENCY_OPTIONS.find(f => f.value === idea.frequency);
    const hours = hoursPerYear(idea);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
                <Chip tone={idea.type === 'bug' ? 'danger' : idea.type === 'data' ? 'info' : 'accent'}>
                    {TYPE_LABEL[idea.type]}
                </Chip>
                <Chip tone="neutral">{idea.area}</Chip>
                <Chip tone="neutral">{idea.roles}</Chip>
            </div>

            <div
                className="flex flex-wrap items-baseline gap-x-6 gap-y-1"
                style={{
                    padding: '10px 12px',
                    borderRadius: 'var(--ds-r-md)',
                    backgroundColor: 'var(--ds-surface-sunken)',
                }}
            >
                <div>
                    <span className="ds-muted">Estimated cost of doing nothing </span>
                    <strong style={{ fontSize: 'var(--ds-fs-lg)' }}>{formatHours(hours)}</strong>
                </div>
                <span className="ds-muted">
                    {idea.peopleAffected} {idea.peopleAffected === 1 ? 'person' : 'people'}
                    {' · '}{freq?.label.toLowerCase() ?? idea.frequency}
                    {' · '}{idea.minutesEach} min each time
                </span>
            </div>

            {idea.type === 'bug' ? (
                <>
                    <Answer question="What happens now">{idea.whatHappens}</Answer>
                    <Answer question="What should happen instead">{idea.whatShould}</Answer>
                    <Answer question="How to make it happen">{idea.repro}</Answer>
                </>
            ) : (
                <>
                    <Answer question="What is it">{idea.whatIsIt}</Answer>
                    <Answer question="What it solves, and for whom">{idea.whoAndWhy}</Answer>
                    <Answer question="How it is done today">{idea.today}</Answer>
                </>
            )}

            <Answer question="What this unlocks">{idea.impact}</Answer>

            <p className="ds-muted m-0">
                Submitted by {idea.submittedBy} on {idea.submittedAt} · {idea.votes.length}{' '}
                {idea.votes.length === 1 ? 'vote' : 'votes'}
            </p>
        </div>
    );
};

export default ImprovementDetail;
